// UserForm.jsx

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, Switch, FormControlLabel } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateUser } from '../../../hooks/useCreateUser';
import { useUpdateUser } from '../../../hooks/useUpdateUser';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { generateRandomString } from '../../../utils/randomString';
import UserOfficesList from '../lists/UserOfficesList';
import { Typography } from '@mui/material';
import UserOfficeForm from './UserOfficeForm';

export default function UserForm({ setUsers, user = null, mode = "create", open, onClose }) {
    const { t } = useTranslation('common'); 

    const [name, setName] = useState(user ? user.name : "");
    const [email, setEmail] = useState(user ? user.email : "");
    const [password, setPassword] = useState(generateRandomString(8));
    const [isDisabled, setIsDisabled] = useState(user ? !!user.disabled : false);

    const [userOfficeFormOpen, setUserOfficeFormOpen] = React.useState(false);
    const [userOfficeFormMode, setUserOfficeFormMode] = React.useState("create");
    const [userListTrigger, setUserListTrigger] = React.useState(false);
    // Toggle function
    const toggleUserListTrigger = () => {
        setUserListTrigger(prev => !prev);
    };


    const { create } = useCreateUser(setUsers);
    const { update } = useUpdateUser(setUsers);
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        setName(user ? user.name : "");
        setEmail(user ? user.email : "");
        setPassword(generateRandomString(8));
        setIsDisabled(user ? !!user.disabled : false); // reset when dialog opens
    }, [user, open]);

    const handleAddUserOffice = () => {
        setUserOfficeFormMode("create");
        setUserOfficeFormOpen(true);
    };

    const handleSave = async () => {
        if (!name.trim()) {
        showSnackbar(t('users.messages.name_required'), "warning");
        return;
        }
        if (!email.trim()) {
        showSnackbar(t('users.messages.email_required'), "warning");
        return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
        showSnackbar(t('users.messages.email_invalid'), "warning");
        return;
        }

        if (mode === "create" && !password.trim()) {
        showSnackbar(t('users.messages.password_required'), "warning");
        return;
        }

        try {
        if (mode === "create") {
            await create({ 
            name,
            email,
            password
            });
            showSnackbar(t('users.messages.create_success'), "success");
        } else if (mode === "edit" && user) {
            await update(user.id, { 
            name,
            email,
            disabled: isDisabled,   // 👈 include disabled state
            });
            showSnackbar(t('users.messages.update_success'), "success");
        }
        setName("");
        setEmail("");
        setPassword("");
        setIsDisabled(false);
        onClose();
        } catch (err) {
        if (err.response?.status === 409 && err.response?.data?.code === "EMAIL_DUPLICATE") {
            showSnackbar(t('users.messages.email_duplicate'), "error");
        } else {
            showSnackbar(t('users.messages.save_fail'), "error");
        }
        }
    };

    const handleCancel = () => {
        setName("");
        setEmail("");
        setPassword("");
        setIsDisabled(false);
        onClose();
    };

    return (
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
        <DialogTitle>{mode === "create" ? "Add User" : "Edit User"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <TextField
                label="User Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
            />

            <TextField
                label="E-mail"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                disabled={mode === "edit"} // 👈 disables field in edit mode
            />

            {mode === "create" && (
                <TextField
                label="Password"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                />
            )}

            {mode === "edit" && (
                <FormControlLabel
                control={
                    <Switch
                    checked={isDisabled}
                    onChange={(e) => setIsDisabled(e.target.checked)}
                    color="primary"
                    />
                }
                label="Disabled"
                />
            )}
            </Stack>
            {mode === "edit" && (
                <>
                <br />
                <Typography variant="h6">Offices</Typography>
                <UserOfficesList 
                    user={user} 
                    trigger={userListTrigger}
                    toggleUserListTrigger={toggleUserListTrigger}
                />
                <Button onClick={handleAddUserOffice}>Add Office</Button>
                <UserOfficeForm 
                    user={user}
                    mode={userOfficeFormMode}
                    open={userOfficeFormOpen}
                    onClose={() => {
                        setUserOfficeFormOpen(false);
                        toggleUserListTrigger();
                    }}
                />
                </>
            )}
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>Save</Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
    );
}